// Single-frame screen capture for the LiveChat "share a screenshot" prompt. The browser's
// native picker is the consent step: getDisplayMedia always shows it and we grab exactly one frame
// from whatever the user chose, then stop the track immediately.

import { DOWNSCALE_MAX_DIMENSION } from './imageUtils';
import { isAllowedByPermissionsPolicy } from './permissionsPolicy';

/** Longest we wait for the first frame after the user picked a surface before giving up. */
export const FRAME_TIMEOUT_MS = 5000;

/** Time given to the host to repaint after it was told the grab is imminent. */
export const SURFACE_SETTLE_MS = 250;

/**
 * True when this browser can run the native screen picker here: the API exists (desktop browsers;
 * not iOS Safari) and the embedding page's Permissions-Policy delegates `display-capture` to us.
 * A host that blocks it, or a stale embed script without `allow="display-capture"`, makes
 * getDisplayMedia reject with the same NotAllowedError as a dismissed picker, so it is checked
 * up front to offer the file-attach path instead.
 */
export const isDisplayCaptureSupported = (): boolean =>
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function' &&
    isAllowedByPermissionsPolicy('display-capture');

/** Thrown when the user closes the picker without choosing anything. */
export class CaptureCancelledError extends Error {
    constructor() {
        super('Screen capture cancelled');
        this.name = 'CaptureCancelledError';
    }
}

export interface CaptureOptions {
    /**
     * Steer the picker to the tab the widget is embedded in (the host application) instead of a
     * list of every window and screen. Chrome-only hints; other browsers ignore them and show their
     * regular picker. Off by default because in standalone mode the current tab is the widget itself.
     */
    preferCurrentTab?: boolean;
    /**
     * Called once the user has picked a surface, right before the frame is grabbed. The stream is
     * live but nothing has been drawn yet, so this is the moment to hide anything that must not
     * appear in the image; a short settle follows so that repaint lands before the grab.
     */
    onSurfaceSelected?: () => void;
}

// Chrome's picker hints beyond what lib.dom types today. Unknown members are ignored by other
// browsers, so this is safe to pass everywhere.
interface ChromeDisplayMediaOptions extends DisplayMediaStreamOptions {
    preferCurrentTab?: boolean;
    selfBrowserSurface?: 'include' | 'exclude';
    surfaceSwitching?: 'include' | 'exclude';
    monitorTypeSurfaces?: 'include' | 'exclude';
}

export const buildDisplayMediaOptions = ({ preferCurrentTab = false }: CaptureOptions): ChromeDisplayMediaOptions =>
    preferCurrentTab
        ? {
              video: { displaySurface: 'browser' },
              audio: false,
              preferCurrentTab: true,
              // preferCurrentTab requires the current tab to be offered, so self must be included.
              selfBrowserSurface: 'include',
              surfaceSwitching: 'exclude',
              monitorTypeSurfaces: 'exclude',
          }
        : { video: true, audio: false };

const isUserCancellation = (err: unknown): boolean =>
    err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError');

// Chromium/Safari expose this; it fires once a frame has actually been composited.
interface VideoFrameCallbackCapable {
    requestVideoFrameCallback?: (callback: () => void) => number;
}

// `loadedmetadata` + `play()` only guarantee dimensions; drawing then can yield a blank image.
// A decoded frame already present is enough. Otherwise wait for a presented frame where the
// browser can say so, else for the first decoded frame. The readyState check comes first because
// the frame callback rides on this page's rendering steps, which pause while the customer's
// chosen tab is in front.
const firstDecodedFrame = (video: HTMLVideoElement): Promise<void> =>
    new Promise<void>((resolve) => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            resolve();
            return;
        }
        const capable = video as HTMLVideoElement & VideoFrameCallbackCapable;
        if (typeof capable.requestVideoFrameCallback === 'function') {
            capable.requestVideoFrameCallback(() => {
                resolve();
            });
            return;
        }
        video.addEventListener(
            'loadeddata',
            () => {
                resolve();
            },
            { once: true },
        );
    });

// Bounded wait: a surface that never delivers a frame, or one the user stops sharing before the
// first frame, would otherwise leave the promise pending forever (tracks open, prompt stuck).
const waitForFrame = (video: HTMLVideoElement, track: MediaStreamTrack | undefined): Promise<void> =>
    new Promise<void>((resolve, reject) => {
        const onEnded = (): void => {
            cleanup();
            reject(new CaptureCancelledError());
        };
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('Timed out waiting for the captured frame'));
        }, FRAME_TIMEOUT_MS);
        const cleanup = (): void => {
            clearTimeout(timer);
            track?.removeEventListener('ended', onEnded);
        };
        track?.addEventListener('ended', onEnded);
        video.onloadedmetadata = () => {
            video
                .play()
                .then(() => firstDecodedFrame(video))
                .then(
                    () => {
                        cleanup();
                        resolve();
                    },
                    (err: unknown) => {
                        cleanup();
                        reject(err instanceof Error ? err : new Error('Failed to play captured stream'));
                    },
                );
        };
        video.onerror = () => {
            cleanup();
            reject(new Error('Failed to load captured stream'));
        };
    }).finally(() => {
        video.onloadedmetadata = null;
        video.onerror = null;
    });

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
            if (b) {
                resolve(b);
            } else {
                reject(new Error('canvas.toBlob returned null'));
            }
        }, 'image/png');
    });

/**
 * Opens the native screen picker and returns one PNG frame of the chosen surface, already scaled
 * to fit {@link DOWNSCALE_MAX_DIMENSION} so a large display never produces a PNG over the upload
 * input cap. Rejects with {@link CaptureCancelledError} when the user dismisses the picker or
 * stops sharing before a frame arrives. The stream is always stopped before returning so the
 * browser's "sharing" indicator disappears immediately.
 */
export const captureDisplayFrame = async (options: CaptureOptions = {}): Promise<Blob> => {
    let stream: MediaStream;
    try {
        stream = await navigator.mediaDevices.getDisplayMedia(buildDisplayMediaOptions(options));
    } catch (err: unknown) {
        if (isUserCancellation(err)) {
            throw new CaptureCancelledError();
        }
        throw err;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    try {
        video.srcObject = stream;
        await waitForFrame(video, stream.getVideoTracks()[0]);
        if (options.onSurfaceSelected) {
            options.onSurfaceSelected();
            await new Promise<void>((resolve) => setTimeout(resolve, SURFACE_SETTLE_MS));
        }

        const scale = Math.min(1, DOWNSCALE_MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Canvas 2d context unavailable');
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        return await canvasToPngBlob(canvas);
    } finally {
        for (const track of stream.getTracks()) {
            track.stop();
        }
        video.srcObject = null;
    }
};
